import Asset from "types/asset";
import { FC, useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectOption from "types/selectOption";
import Utilities from "utils/utilities";
import frequencyConfList from "types/frequencyConf";
import ConfirmatioDialog from "ui-component/ConfirmationDialog";
import Tag from "types/tag";
import { v4 as uuidv4 } from "uuid";
import TagService from "services/TagService";
import useAuth from "hooks/useAuth";
import { dispatch } from "store";
import { startSnackbar } from "store/slices/snackbar";

interface ChildComponentProps {
  asset?: Asset;

  onSave(asset: Asset): any;

  onDelete(asset: Asset): any;

  onClose(): any;
}

// Default threshold values
const DEFAULT_THRESHOLDS = {
  minTemperature: -20,
  maxTemperature: 60,
  minHumidity: 0,
  maxHumidity: 90,
  minPressure: 600,
  maxPressure: 1100,
};

const AssetEdit: FC<ChildComponentProps> = (props) => {
  const { asset, onSave, onDelete, onClose } = props;
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [currentAsset, setCurrentAsset] = useState<Asset | undefined>(asset);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagToSearch, setTagToSearch] = useState<string>("");
  const theme = useTheme();
  const { t } = useTranslation();
  const [dialogDelete, setDialogDelete] = useState(false);
  const utilities = new Utilities();
  const frequencyConf: SelectOption[] = frequencyConfList(t);
  const tagService = new TagService();
  const [acceleration, setAcceleration] = useState<string>("");
  const [removedTags, setRemovedTags] = useState<Tag[]>([]);

  // Threshold states with default values (removed acceleration thresholds)
  const [maxCelsiusTemperatureThreshold, setMaxCelsiusTemperatureThreshold] =
    useState<string>("");
  const [maxHumidityThreshold, setMaxHumidityThreshold] = useState<string>("");
  const [maxPressureThreshold, setMaxPressureThreshold] = useState<string>("");
  const [minCelsiusTemperatureThreshold, setMinCelsiusTemperatureThreshold] =
    useState<string>("");
  const [minHumidityThreshold, setMinHumidityThreshold] = useState<string>("");
  const [minPressureThreshold, setMinPressureThreshold] = useState<string>("");

  // Luminosity alert state
  const [isLuminosityAlertActive, setIsLuminosityAlertActive] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (asset?.acceleration !== undefined) {
        setAcceleration(asset.acceleration.toFixed(2));
      }

      // Initialize threshold values with defaults if not present (removed acceleration thresholds)
      setMaxCelsiusTemperatureThreshold(
        asset?.maxCelsiusTemperatureThreshold?.toString() ||
        DEFAULT_THRESHOLDS.maxTemperature.toString()
      );
      setMaxHumidityThreshold(
        asset?.maxHumidityThreshold?.toString() ||
        DEFAULT_THRESHOLDS.maxHumidity.toString()
      );
      setMaxPressureThreshold(
        asset?.maxPressureThreshold?.toString() ||
        DEFAULT_THRESHOLDS.maxPressure.toString()
      );
      setMinCelsiusTemperatureThreshold(
        asset?.minCelsiusTemperatureThreshold?.toString() ||
        DEFAULT_THRESHOLDS.minTemperature.toString()
      );
      setMinHumidityThreshold(
        asset?.minHumidityThreshold?.toString() ||
        DEFAULT_THRESHOLDS.minHumidity.toString()
      );
      setMinPressureThreshold(
        asset?.minPressureThreshold?.toString() ||
        DEFAULT_THRESHOLDS.minPressure.toString()
      );

      // Initialize luminosity alert state
      setIsLuminosityAlertActive(asset?.isLuminosityAlertActive || false);

      if (asset === undefined) setOpen(false);
      else {
        setOpen(true);
        setCurrentAsset(asset);
        await fetchTags();
        setSelectedTags(asset.tags);
      }
    })();
  }, [asset]);

  useEffect(() => {
    const e = {
      ...currentAsset!,
      tags: selectedTags,
      maxCelsiusTemperatureThreshold: maxCelsiusTemperatureThreshold
        ? parseFloat(maxCelsiusTemperatureThreshold)
        : DEFAULT_THRESHOLDS.maxTemperature,
      maxHumidityThreshold: maxHumidityThreshold
        ? parseFloat(maxHumidityThreshold)
        : DEFAULT_THRESHOLDS.maxHumidity,
      maxPressureThreshold: maxPressureThreshold
        ? parseFloat(maxPressureThreshold)
        : DEFAULT_THRESHOLDS.maxPressure,
      minCelsiusTemperatureThreshold: minCelsiusTemperatureThreshold
        ? parseFloat(minCelsiusTemperatureThreshold)
        : DEFAULT_THRESHOLDS.minTemperature,
      minHumidityThreshold: minHumidityThreshold
        ? parseFloat(minHumidityThreshold)
        : DEFAULT_THRESHOLDS.minHumidity,
      minPressureThreshold: minPressureThreshold
        ? parseFloat(minPressureThreshold)
        : DEFAULT_THRESHOLDS.minPressure,
      isLuminosityAlertActive: isLuminosityAlertActive,
    };
    setCurrentAsset(e);
  }, [
    selectedTags,
    maxCelsiusTemperatureThreshold,
    maxHumidityThreshold,
    maxPressureThreshold,
    minCelsiusTemperatureThreshold,
    minHumidityThreshold,
    minPressureThreshold,
    isLuminosityAlertActive,
  ]);

  useEffect(() => {
    (async () => {
      if (asset !== undefined) {
        await fetchTags();
      }
    })();
  }, [asset, tagToSearch]);

  const fetchTags = useCallback(
    debounce(async () => {
      if (asset && asset.id) {
        const tagResult = await tagService.getAssetTags(
          tagToSearch,
          asset!.id,
          user?.tenantId!
        );
        setTags(tagResult);
      }
    }, 700),
    [tagToSearch, user?.tenantId, asset]
  );

  const handleTagChange = (_: React.SyntheticEvent, newValue: Tag[]) => {
    const uniqueTags = newValue
    .map((tag) => ({ ...tag, name: tag.name.trim() }))
    .filter(
      (tag, index, self) =>
        index ===
        self.findIndex((t) => t.name.toLowerCase() === tag.name.toLowerCase())
    );
    setSelectedTags(uniqueTags);

    const removedTags = selectedTags.filter(
      (tag) => !uniqueTags.some((selectedTag) => selectedTag.id === tag.id)
    );
    setRemovedTags((prev) => {
      const updatedTags = [...prev, ...removedTags].filter(
        (tag, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.id === tag.id ||
              t.name.trim().toLowerCase() === tag.name.trim().toLowerCase()
          )
      );
      return updatedTags;
    });
    setTags((prev) => {
      const uniqueTags = [...prev, ...removedTags].filter(
        (tag, index, self) =>
          index ===
          self.findIndex(
            (t) => t.name.trim().toLowerCase() === tag.name.trim().toLowerCase()
          )
      );
      return uniqueTags;
    });
  };

  const handleTagInputChange = (
    event: React.KeyboardEvent | React.FocusEvent,
    value: string
  ) => {
    const trimmedValue = value.trim().toLowerCase();
    const containsComma = trimmedValue.includes(",");

    if (containsComma) {
      dispatch(startSnackbar(t("ERROR_ADDING_TAG_REMOVE_COMMA"), "error"));
      return;
    }

    setTagToSearch(value);

    // Verifica se l'input è valido
    if (
      trimmedValue &&
      !containsComma &&
      (event.type === "blur" || (event as React.KeyboardEvent).key === "Enter")
    ) {
      const existingTag = tags.find(
        (tag) => tag.name.trim().toLowerCase() === trimmedValue
      );
      const alreadySelected = selectedTags.some(
        (tag) => tag.name.trim().toLowerCase() === trimmedValue
      );

      if (!alreadySelected) {
        if (existingTag) {
          setRemovedTags((prev) =>
            prev.filter((tag) => tag.id !== existingTag.id)
          );
          setSelectedTags((prev) => [...prev, existingTag]);
        } else {
          const newTag: Tag = { id: uuidv4(), name: trimmedValue };
          setTags((prev) => [...prev, newTag]);
          setSelectedTags((prev) => [...prev, newTag]);
        }
      }
    }
  };

  const handleFrequency = (e: any) => {
    const { value } = utilities.getFormValue(e);
    const assetCopy = {
      ...currentAsset!,
      frequency: parseFloat(value),
    };
    setCurrentAsset(assetCopy);
  };

  const handleThresholdChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    propertyName: string
  ) => {
    setter(value);
    const updatedAsset = {
      ...currentAsset!,
      [propertyName]: value === "" ? undefined : parseFloat(value),
    };
    setCurrentAsset(updatedAsset);
  };

  const handleLuminosityAlertChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsLuminosityAlertActive(checked);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {t("EDIT")} asset {asset?.name}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "6px!important" }}>
          <Grid container spacing={2}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}
                >
                  📋 {t("BASIC_INFORMATION")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>{t("NAME")}</InputLabel>
                <OutlinedInput
                  size="small"
                  defaultValue={asset?.name}
                  onChange={(d) => {
                    const e = {
                      ...currentAsset!,
                      name: d.target.value,
                    };
                    setCurrentAsset(e);
                  }}
                  label={t("NAME")}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                autoHighlight
                id="asset-tags-autocomplete"
                size="small"
                options={[
                  ...tags,
                  ...removedTags.filter(
                    (tag) =>
                      !tags.some(
                        (existingTag) =>
                          existingTag.name.trim().toLowerCase() ===
                          tag.name.trim().toLowerCase()
                      )
                  ),
                ].filter(
                  (tag) =>
                    !selectedTags.some(
                      (selected) =>
                        selected.name.trim().toLowerCase() ===
                        tag.name.trim().toLowerCase()
                    )
                )}
                getOptionLabel={(option) => option.name}
                value={selectedTags}
                onChange={handleTagChange}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        color="secondary"
                        key={key}
                        label={option.name}
                        size="small"
                        {...tagProps}
                      />
                    );
                  })
                }
                onInputChange={(event: any, value, reason) => {
                  if (reason === "input") handleTagInputChange(event, value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    onKeyDown={(event: any) =>
                      handleTagInputChange(event, event.target.value)
                    }
                    onBlur={(event) =>
                      handleTagInputChange(event, event.target.value)
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={0.5}>
            {/* NUOVA SEZIONE: Startup Parameters */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: "bold",
                  }}
                >
                  ⚡ {t("STARTUP_PARAMETERS")}
                </Typography>
              </Box>
            </Grid>

            {/* Frequency and Acceleration Parameters moved here */}
            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                🔄 {t("FREQUENCY")}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t("FREQUENCY")}</InputLabel>
                <Select
                  size="small"
                  disabled={!!currentAsset?.halogPairDate}
                  label={t("FREQUENCY")}
                  name="frequency"
                  onChange={handleFrequency}
                  value={Math.floor(currentAsset?.frequency || 2)}
                >
                  {frequencyConf.map((frequency) => (
                    <MenuItem value={frequency.value} key={frequency.value}>
                      {t(frequency.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                ⚡ {t("ACCELERATION")} (G)
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t("ACCELERATION")}</InputLabel>
                <OutlinedInput
                  size="small"
                  disabled={!!currentAsset?.halogPairDate}
                  value={acceleration}
                  onBlur={(e) => {
                    let value = parseFloat(e.target.value || "0").toFixed(2);
                    if (isNaN(Number(value))) {
                      value = "0";
                    }
                    setAcceleration(value);
                    const updatedAsset = {
                      ...currentAsset!,
                      acceleration: parseFloat(value),
                    };
                    setCurrentAsset(updatedAsset);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAcceleration(value);
                  }}
                  type="number"
                  label={t("ACCELERATION")}
                  inputProps={{ step: "1", min: "0" }}
                />
              </FormControl>
            </Grid>

            {/* Operating Parameters Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.secondary.main}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.secondary.main,
                    fontWeight: "bold",
                  }}
                >
                  ⚙️ {t("OPERATING_PARAMETERS")}
                </Typography>
              </Box>
            </Grid>

            {/* Luminosity Alert Switch */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                💡 {t("LUMINOSITY_PARAMETERS")}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isLuminosityAlertActive}
                    onChange={handleLuminosityAlertChange}
                    color="primary"
                  />
                }
                label={t("LUMINOSITY_ALERT_ACTIVE")}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                  }
                }}
              />
            </Grid>

            {/* Temperature Parameters */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                🌡️ {t("TEMPERATURE_PARAMETERS")} (°C)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MIN_TEMPERATURE_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={minCelsiusTemperatureThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMinCelsiusTemperatureThreshold,
                      "minCelsiusTemperatureThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1" }}
                  label={t("MIN_TEMPERATURE_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minTemperature}°C`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MAX_TEMPERATURE_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={maxCelsiusTemperatureThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMaxCelsiusTemperatureThreshold,
                      "maxCelsiusTemperatureThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1" }}
                  label={t("MAX_TEMPERATURE_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxTemperature}°C`}
                  size="small"
                />
              </FormControl>
            </Grid>

            {/* Humidity Parameters */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                💧 {t("HUMIDITY_PARAMETERS")} (%)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MIN_HUMIDITY_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={minHumidityThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMinHumidityThreshold,
                      "minHumidityThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1", min: "0", max: "100" }}
                  label={t("MIN_HUMIDITY_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minHumidity}%`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MAX_HUMIDITY_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={maxHumidityThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMaxHumidityThreshold,
                      "maxHumidityThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1", min: "0", max: "100" }}
                  label={t("MAX_HUMIDITY_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxHumidity}%`}
                  size="small"
                />
              </FormControl>
            </Grid>

            {/* Pressure Parameters */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                📊 {t("PRESSURE_PARAMETERS")} (hPa)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MIN_PRESSURE_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={minPressureThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMinPressureThreshold,
                      "minPressureThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1" }}
                  label={t("MIN_PRESSURE_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.minPressure} hPa`}
                  size="small"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("MAX_PRESSURE_PARAMETER")}</InputLabel>
                <OutlinedInput
                  value={maxPressureThreshold}
                  onChange={(e) =>
                    handleThresholdChange(
                      e.target.value,
                      setMaxPressureThreshold,
                      "maxPressureThreshold"
                    )
                  }
                  type="number"
                  inputProps={{ step: "1" }}
                  label={t("MAX_PRESSURE_PARAMETER")}
                  placeholder={`Default: ${DEFAULT_THRESHOLDS.maxPressure} hPa`}
                  size="small"
                />
              </FormControl>
            </Grid>
          </Grid>
          <Typography
            marginTop="15px"
            sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary }}
          >
            * {t("MANDATORY_FIELDS")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Grid
            container
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              paddingLeft: "15px",
              paddingRight: "15px",
            }}
          >
            <Grid item>
              <Button
                style={{
                  backgroundColor: theme.palette.error.main,
                  borderRadius: 7,
                  border: "2px solid",
                  borderColor: theme.palette.error.main,
                }}
                onClick={() => setDialogDelete(true)}
              >
                <Typography sx={{ color: "white" }}> {t("DELETE")}</Typography>
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={() => {
                  setTags([]);
                  setSelectedTags([]);
                  onClose();
                }}
                autoFocus
                style={{
                  border: "2px solid",
                  borderColor: theme.palette.secondary.main,
                  borderRadius: 7,
                  marginRight: 10,
                }}
              >
                <Typography sx={{ color: theme.palette.primary.main }}>
                  {" "}
                  {t("CANCEL")}
                </Typography>
              </Button>
              <Button
                onClick={() => {
                  setTags([]);
                  setSelectedTags([]);
                  onSave(currentAsset!);
                }}
                style={{
                  border: "2px solid",
                  borderColor: theme.palette.primary.main,
                  borderRadius: 7,
                  background: theme.palette.primary.main,
                }}
              >
                <Typography sx={{ color: "#fff" }}> {t("SAVE")}</Typography>
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <ConfirmatioDialog
        isVisible={dialogDelete}
        onClose={() => {
          setDialogDelete(false);
        }}
        onConfirm={() => {
          setDialogDelete(false);
          onDelete(asset!);
        }}
      />
    </>
  );
};

export default AssetEdit;
